import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  Brain, 
  Database, 
  Mail, 
  FileText, 
  Menu as MenuIcon, 
  Settings, 
  Globe, 
  ChevronDown, 
  ChevronRight,
  LogOut,
  BarChart3,
  Wrench,
  List,
  Server,
  Languages,
  Zap,
  Scan,
  Image,
  Shield,
  Package,
  TrendingUp,
  Activity
} from 'lucide-react';
import TenantManagement from './TenantManagement/TenantManagement';
import UserManagement from './UserManagement/UserManagement';
import SystemOverview from './SystemMonitoring/SystemOverview';
import DatabaseValidation from './SystemMonitoring/DatabaseValidation';
import PerformanceMonitoring from './SystemMonitoring/PerformanceMonitoring';
import GlobalAIConfig from './GlobalConfiguration/GlobalAIConfig';
import AIModelManagement from './GlobalConfiguration/AIModelManagement';
import GlobalEmailConfig from './GlobalConfiguration/GlobalEmailConfig';
import GlobalMenuConfig from './GlobalConfiguration/GlobalMenuConfig';
import EmailLog from './SystemMonitoring/EmailLog';
import OCRManagement from './SystemMonitoring/OCRManagement';
import ClassificationManagement from './SystemMonitoring/ClassificationManagement';
import SchemaManagement from './EntityConfiguration/SchemaManagement';
import EntityConfigManager from './EntityConfiguration/EntityConfigManager';
import TenantEntityConfig from './TenantManagement/TenantEntityConfig';
import GlobalListValuesManagement from './GlobalConfiguration/GlobalListValuesManagement';
import ListingConfigManager from '../TenantAdmin/ListingConfigManager';
import GlobalListingConfigManager from './GlobalConfiguration/GlobalListingConfigManager';
import LabelManager from './LabelManager';
import AutomationManagement from './AutomationManagement/AutomationManagement';
import DataProviderManagement from './DataProviderManagement';
import EntityEntitlementManager from './EntityEntitlementManagement/EntityEntitlementManager';
import PackageManagement from './PackageManagement/PackageManagement';
import TenantUsageDashboard from './TenantUsageDashboard/TenantUsageDashboard';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    tenants: true,
    global: true,
    entity: true,
    monitoring: true,
    dataConfig: true,
    dataProviders: true
  });
  const { user, logout } = useAuth();
  const { setDarkTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user is admin
    if (!user || user.userType !== 'admin') {
      navigate('/login');
      return;
    }
    
    // Set dark theme as default for admin pages
    setDarkTheme();
    
    // Handle URL parameters for tab selection
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam && ['overview', 'tenants', 'users', 'globalAi', 'aiModels', 'database', 'performanceMonitoring', 'emailConfig', 'emailLogs', 'schema', 'globalMenu', 'entityConfig', 'tenantEntityConfig', 'dataListValues', 'automationManagement', 'ocrManagement', 'classificationManagement', 'dataProviders', 'entityEntitlements', 'packageManagement', 'tenantUsageDashboard'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    
    setLoading(false);
  }, [user, navigate, setDarkTheme, location.search]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const menuSections = [
    {
      id: 'overview',
      title: 'Dashboard',
      icon: LayoutDashboard,
      items: [
        { id: 'overview', label: 'System Overview', icon: BarChart3 }
      ]
    },
    {
      id: 'tenants',
      title: 'Tenant Management',
      icon: Building2,
      items: [
        { id: 'tenants', label: 'Tenants', icon: Building2 },
        { id: 'users', label: 'Users', icon: Users },
        { id: 'packageManagement', label: 'Package Management', icon: Package, superAdminOnly: true },
        { id: 'tenantUsageDashboard', label: 'Usage Dashboard', icon: TrendingUp, superAdminOnly: true }
      ]
    },
    {
      id: 'global',
      title: 'System Configuration',
      icon: Globe,
      adminOnly: true,
      items: [
        { id: 'globalAi', label: 'AI Configuration', icon: Brain },
        { id: 'aiModels', label: 'AI Model Management', icon: Brain, superAdminOnly: true },
        { id: 'dataProviders', label: 'Data Providers', icon: Database },
        { id: 'emailConfig', label: 'Email Config', icon: Mail },
        { id: 'globalMenu', label: 'Menu Config', icon: MenuIcon },
        { id: 'automationManagement', label: 'Automation Management', icon: Zap, superAdminOnly: true },
        { id: 'entityEntitlements', label: 'Entity Entitlements', icon: Shield, superAdminOnly: true }
      ]
    },
    {
      id: 'entity',
      title: 'Entity Configuration',
      icon: Database,
      adminOnly: true,
      items: [
        { id: 'schema', label: 'Schema Management', icon: Server },
        { id: 'entityConfig', label: 'Entity Config', icon: Settings }
      ]
    },
    {
      id: 'monitoring',
      title: 'System Monitoring',
      icon: Wrench,
      adminOnly: true,
      items: [
        { id: 'performanceMonitoring', label: 'Performance Monitoring', icon: Activity },
        { id: 'database', label: 'Database Validation', icon: Database },
        { id: 'emailLogs', label: 'Email Logs', icon: FileText },
        { id: 'ocrManagement', label: 'OCR Management', icon: Scan },
        { id: 'classificationManagement', label: 'Image Classification', icon: Image }
      ]
    },
    {
      id: 'dataConfig',
      title: 'Data Configuration',
      icon: Settings,
      items: [
        { id: 'tenantEntityConfig', label: 'Entity Config', icon: Settings },
        { id: 'dataListValues', label: 'List Values', icon: List },
        { id: 'listingConfig', label: 'List Configuration', icon: List },
        { id: 'globalListingConfig', label: 'Global List Config', icon: Globe }
      ]
    },
    {
      id: 'labels',
      title: 'Label Management',
      icon: Languages,
      adminOnly: true,
      items: [
        { id: 'labelManager', label: 'Label Manager', icon: Languages }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <header className="admin-header-modern">
        <div className="admin-header-left">
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <MenuIcon size={24} />
          </button>
          <div className="admin-title-modern">
            <h1>CRM Administration</h1>
          </div>
        </div>
        <div className="admin-header-right">
          <div className="admin-user-info-modern">
            <div className="user-avatar-modern">
              {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
            </div>
            <div className="user-details-modern">
              <span className="user-name-modern">
                {user.firstName} {user.lastName}
              </span>
              <span className="user-role-modern">
                {user.role} {user.isSuperAdmin && '(Super)'}
              </span>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn-modern">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="admin-layout">
        {/* Sidebar */}
        <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <nav className="sidebar-nav">
            {menuSections.map(section => {
              // Skip admin-only sections for non-admin users
              if (section.adminOnly && user.userType !== 'admin' && !user.isSuperAdmin) {
                return null;
              }

              const SectionIcon = section.icon;
              const isExpanded = expandedSections[section.id];

              return (
                <div key={section.id} className="nav-section">
                  {section.items.length > 1 ? (
                    <>
                      <button 
                        className="nav-section-header"
                        onClick={() => toggleSection(section.id)}
                      >
                        <div className="nav-section-title">
                          <SectionIcon size={18} />
                          {!sidebarCollapsed && <span>{section.title}</span>}
                        </div>
                        {!sidebarCollapsed && (
                          isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="nav-section-items">
                          {section.items.map(item => {
                            // Skip super admin only items for non-super admin users
                            if (item.superAdminOnly && !user.isSuperAdmin) {
                              return null;
                            }
                            
                            const ItemIcon = item.icon;
                            return (
                              <button
                                key={item.id}
                                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(item.id)}
                                title={sidebarCollapsed ? item.label : ''}
                              >
                                <ItemIcon size={18} />
                                {!sidebarCollapsed && <span>{item.label}</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      className={`nav-item single ${activeTab === section.items[0].id ? 'active' : ''}`}
                      onClick={() => setActiveTab(section.items[0].id)}
                      title={sidebarCollapsed ? section.items[0].label : ''}
                    >
                      <SectionIcon size={18} />
                      {!sidebarCollapsed && <span>{section.items[0].label}</span>}
                    </button>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main-modern">
          <div className="admin-content-modern">
            {activeTab === 'overview' && <SystemOverview />}
            {activeTab === 'tenants' && <TenantManagement />}
            {activeTab === 'users' && <UserManagement />}
            {activeTab === 'globalAi' && <GlobalAIConfig />}
            {activeTab === 'aiModels' && <AIModelManagement />}
            {activeTab === 'dataProviders' && <DataProviderManagement />}
            {activeTab === 'database' && <DatabaseValidation />}
            {activeTab === 'performanceMonitoring' && <PerformanceMonitoring />}
            {activeTab === 'emailConfig' && <GlobalEmailConfig />}
            {activeTab === 'emailLogs' && <EmailLog />}
            {activeTab === 'ocrManagement' && <OCRManagement />}
            {activeTab === 'classificationManagement' && <ClassificationManagement />}
            {activeTab === 'globalMenu' && <GlobalMenuConfig />}
            {activeTab === 'automationManagement' && <AutomationManagement />}
            {activeTab === 'schema' && <SchemaManagement />}
            {activeTab === 'entityConfig' && <EntityConfigManager />}
            {activeTab === 'tenantEntityConfig' && <TenantEntityConfig />}
            {activeTab === 'dataListValues' && <GlobalListValuesManagement />}
            {activeTab === 'listingConfig' && <ListingConfigManager />}
            {activeTab === 'globalListingConfig' && <GlobalListingConfigManager />}
            {activeTab === 'labelManager' && <LabelManager />}
            {activeTab === 'entityEntitlements' && <EntityEntitlementManager />}
            {activeTab === 'packageManagement' && <PackageManagement />}
            {activeTab === 'tenantUsageDashboard' && <TenantUsageDashboard />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;